import {
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useGetRecordRepresentation,
  useNotify,
  useTranslate,
} from "ra-core";

import { useQueryClient } from "@tanstack/react-query";

import { useEffect, useMemo, useState } from "react";

import { CreateSheet } from "../misc/CreateSheet";

import { TaskFormContent } from "./TaskFormContent";

export interface TaskCreateSheetProps {
  open: boolean;

  onOpenChange: (open: boolean) => void;

  contact_id?: Identifier;
}

export const TaskCreateSheet = ({
  open,
  onOpenChange,
  contact_id,
}: TaskCreateSheetProps) => {
  const { identity } = useGetIdentity();

  const translate = useTranslate();

  const notify = useNotify();

  const dataProvider = useDataProvider();

  const queryClient = useQueryClient();

  const getContactRepresentation = useGetRecordRepresentation("contacts");

  const [selectedContactIds, setSelectedContactIds] = useState<Identifier[]>(
    contact_id != null ? [contact_id] : [],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedContactIds(contact_id != null ? [contact_id] : []);
  }, [open, contact_id]);

  const { data: contact } = useGetOne(
    "contacts",
    {
      id: contact_id!,
    },
    {
      enabled: contact_id != null,
    },
  );

  const initialRecord = useMemo(
    () => ({
      type: "",

      status: "new",

      contact_id: contact_id ?? null,

      due_date: new Date(Date.now() + 3600000).toISOString(),

      sales_id: identity?.id,

      created_by_sales_id: identity?.id,
    }),
    // Reopening starts a new draft; contact selection must not reset entered fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, contact_id, identity?.id],
  );
  if (!identity) {
    return null;
  }

  const handleSuccess = async (task: any) => {
    try {
      if (selectedContactIds.length > 0) {
        await Promise.all(
          selectedContactIds.map((selectedContactId) =>
            dataProvider.create("task_contacts", {
              data: {
                task_id: task.id,

                contact_id: selectedContactId,
              },
            }),
          ),
        );

        await Promise.all(
          selectedContactIds.map(async (selectedContactId) => {
            try {
              const { data: currentContact } = await dataProvider.getOne(
                "contacts",
                {
                  id: selectedContactId,
                },
              );

              await dataProvider.update("contacts", {
                id: selectedContactId,

                data: {
                  last_seen: new Date().toISOString(),
                },

                previousData: currentContact,
              });
            } catch (error) {
              console.error("Failed to update contact last_seen", error);
            }
          }),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ["task_contacts"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["contact_tasks"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["tasks"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });

      notify("Задача создана", {
        type: "success",
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to link contacts to task", error);

      notify("Задача создана, но не все контакты удалось привязать", {
        type: "warning",
      });

      onOpenChange(false);
    }
  };

  return (
    <CreateSheet
      resource="tasks"
      title={
        contact
          ? translate("resources.tasks.dialog.create_for", {
              name: getContactRepresentation(contact),
            })
          : translate("resources.tasks.dialog.create")
      }
      redirect={false}
      record={initialRecord}
      mutationOptions={{
        onSuccess: handleSuccess,
      }}
      open={open}
      onOpenChange={onOpenChange}
    >
      <TaskFormContent
        selectedContactIds={selectedContactIds}
        onSelectedContactIdsChange={setSelectedContactIds}
      />
    </CreateSheet>
  );
};
